import { useState } from 'react';
import { Box, Container, Typography, Button, Stack, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import StarIcon from '@mui/icons-material/Star';
import EmailIcon from '@mui/icons-material/Email';
import LoginIcon from '@mui/icons-material/Login';
import { useNavigate } from 'react-router-dom';
import HeroImage from '../images/HeroImage.png';
import Logo from '../images/logo.png';
import { trackWaitlistSignup, trackButtonClick } from '../utils/analytics';

const HeroContainer = styled(Box)(() => ({
  background: '#064E3B',
  minHeight: '75vh',
  display: 'flex',
  alignItems: 'flex-start',
  paddingTop: '60px',
  paddingBottom: '20px',
  position: 'relative',
  overflow: 'hidden'
}));

const FloatingElement = styled(Box)(() => ({
  position: 'absolute',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  animation: 'float 6s ease-in-out infinite',
  '@keyframes float': {
    '0%, 100%': { transform: 'translateY(0px)' },
    '50%': { transform: 'translateY(-20px)' }
  }
}));


const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: (theme.shape.borderRadius as number) * 2,
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)'
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)'
    },
    '&.Mui-focused fieldset': {
      borderColor: 'white'
    }
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary
  }
}));

const HeroSection = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Track form submission attempt
    trackButtonClick('Waitlist Form Submit', 'HeroSection');
    
    if (!email) {
      setError('Please enter your email address');
      // Track validation error
      trackButtonClick('Waitlist Validation Error - Empty Email', 'HeroSection');
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      // Track validation error
      trackButtonClick('Waitlist Validation Error - Invalid Email', 'HeroSection');
      return;
    }
    
    try {
      setError('');
      
      // Track API call attempt
      trackButtonClick('Waitlist API Call Started', 'HeroSection');
      
      // Call our API function
      const response = await fetch('/api/join-waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
        setEmail(''); // Clear the email field
        // Track successful waitlist signup
        trackWaitlistSignup(email);
        // Track successful completion
        trackButtonClick('Waitlist Signup Success', 'HeroSection');
      } else {
        setError(data.error || 'Failed to join waitlist. Please try again.');
        // Track API error
        trackButtonClick('Waitlist API Error', 'HeroSection');
      }
    } catch (error) {
      console.error('Error joining waitlist:', error);
      setError('Network error. Please check your connection and try again.');
      // Track network error
      trackButtonClick('Waitlist Network Error', 'HeroSection');
    }
  };

  return (
    <HeroContainer>
      <FloatingElement sx={{ width: 80, height: 80, top: '10%', left: '10%', animationDelay: '0s' }} />
      <FloatingElement sx={{ width: 60, height: 60, top: '20%', right: '15%', animationDelay: '2s' }} />
      <FloatingElement sx={{ width: 100, height: 100, bottom: '20%', left: '5%', animationDelay: '4s' }} />
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Logo and Brand - Top Left Corner */}
        <Box sx={{ position: 'absolute', top: 20, left: 20, zIndex: 10 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 42,
                height: 42,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                p: 0.5
              }}
            >
              <img src={Logo} alt="RiteReview Logo" style={{ width: '100%', height: '100%' }} />
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                letterSpacing: 0.3,
                color: '#fbbf24',
                fontSize: '2rem',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              RiteReview
            </Typography>
          </Stack>
        </Box>

        {/* Login Button - Top Right Corner */}
        <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 10 }}>
          <Button
            variant="contained"
            startIcon={<LoginIcon />}
            onClick={() => {
              trackButtonClick('Login Button', 'HeroSection');
              navigate('/signin');
            }}
            sx={{
              bgcolor: '#FBBF24',
              color: '#1F2937',
              fontWeight: 600,
              px: 3,
              py: 1.5,
              borderRadius: '50px',
              boxShadow: '0 6px 20px rgba(251, 191, 36, 0.3)',
              textTransform: 'none',
              fontSize: '1rem',
              '&:hover': {
                bgcolor: '#F59E0B',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(251, 191, 36, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Login
          </Button>
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={6} alignItems="center" sx={{ pt: 8 }}>
          <Box flex={1}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <StarIcon sx={{ color: '#FFD700', fontSize: 24 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                Review Automation Platform
              </Typography>
            </Stack>
            
            <Typography 
              component="h1"
              variant="h1" 
              sx={{ 
                color: 'white', 
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' }
              }}
            >
              Boost Your Online Presence with{' '}
              <Box component="span" sx={{ 
                color: '#fbbf24'
              }}>
                RiteReview
              </Box>
            </Typography>
            
            <Typography 
              component="p"
              variant="h5" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)', 
                mb: 4, 
                fontWeight: 400,
                lineHeight: 1.6
              }}
            >
              Transform your customer feedback into powerful social proof. 
              Automate review generation, set your own customer rewards, and watch your online reputation soar.
            </Typography>
            
            {!submitted ? (
              <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 500 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="stretch">
                  <StyledTextField
                    fullWidth
                    type="email"
                    label="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => trackButtonClick('Email Field Focus', 'HeroSection')}
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                  
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    onClick={() => trackButtonClick('Join Waitlist', 'HeroSection')}
                    sx={{
                      bgcolor: '#FBBF24',
                      color: '#1F2937',
                      px: 4,
                      py: 2,
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderRadius: '50px',
                      minWidth: { xs: 'auto', sm: 200 },
                      '&:hover': {
                        bgcolor: '#F59E0B',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Join Waitlist
                  </Button>
                </Stack>
                
                {error && (
                  <Typography variant="body2" sx={{ color: '#ff6b6b', mt: 2, textAlign: 'center' }}>
                    {error}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
                <Typography variant="h5" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                  🎉 You're on the list!
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  We'll notify you as soon as RiteReview launches. Get ready to boost your reviews!
                </Typography>
              </Box>
            )}
            
            {/* <GlassCard sx={{ mt: 4 }}>
              <Stack direction="row" spacing={4} alignItems="center">
                <Box textAlign="center">
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                    10K+
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Reviews Generated
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                    500+
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Happy Businesses
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                    95%
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Success Rate
                  </Typography>
                </Box>
              </Stack>
            </GlassCard> */}
          </Box>
          
          <Box flex={1} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box
              component="img"
              src={HeroImage}
              alt="RiteReview App Interface"
              sx={{
                width: '100%',
                height: 'auto',
                maxWidth: 500,
                borderRadius: 3,
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
                animation: 'float 8s ease-in-out infinite',
                animationDelay: '1s'
              }}
            />
          </Box>
        </Stack>
      </Container>
    </HeroContainer>
  );
};

export default HeroSection;